"use client";

import { AppleLogo } from "@/components/icons/AppleLogo";
import { GooglePlayLogo } from "@/components/icons/GooglePlayLogo";
import { WindowsLogo } from "@/components/icons/WindowsLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useQuestAnalytics } from "@/context/AnalyticsProvider/quests";
import { cn } from "@/lib/utils";
import { formatGenreLabel } from "@/utils/string";
import debounce from "lodash/debounce";
import { Check, ChevronDown, Crown, Gem, Globe, Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type FilterOptions, useQuestFilter } from "./QuestFilterProvider";

// Platform icons for OS-based filtering with real logos
const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case "Windows":
      return <WindowsLogo className="w-4 h-4" />;
    case "iOS":
      return <AppleLogo className="w-4 h-4" />;
    case "Android":
      return <GooglePlayLogo className="w-4 h-4" />;
    case "Web":
      return <Globe className="w-4 h-4" />;
    case "macOS":
      return <AppleLogo className="w-4 h-4" />;
    default:
      return <Globe className="w-4 h-4" />;
  }
};

const getEarningsLabel = (reward: string): string => {
  const labelMap: Record<string, string> = {
    point: "Gems",
    weekly_point: "Weekly Points",
  };
  return labelMap[reward.toLowerCase()] || reward;
};

const getEarningsIcon = (reward: string) => {
  const normalized = reward.toLowerCase();
  if (normalized === "point") return <Gem className="w-4 h-4" />;
  if (normalized === "weekly_point") return <Crown className="w-4 h-4" />;
  return null;
};

function FilterSection({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

interface FilterChipProps {
  label: string;
  selected: boolean;
  onToggle: () => void;
  icon?: React.ReactNode;
  removable?: boolean;
}

function FilterChip({
  label,
  selected,
  onToggle,
  icon,
  removable = false,
}: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
        selected
          ? "bg-white text-neutral-900"
          : "bg-white/10 text-white hover:bg-white/20 border border-white/20",
      )}
    >
      {icon && <span className="[&>*]:w-3 [&>*]:h-3">{icon}</span>}
      <span>{label}</span>
      {selected && removable && (
        <X className="w-3 h-3 ml-0.5" strokeWidth={2.5} />
      )}
    </button>
  );
}

function ToggleSwitch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between cursor-pointer group">
      <span className="text-sm text-white/70 group-hover:text-white transition-colors">
        {label}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
          checked ? "bg-white" : "bg-white/20",
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 transform rounded-full transition-transform",
            checked ? "translate-x-6 bg-neutral-900" : "translate-x-1 bg-white",
          )}
        />
      </button>
    </div>
  );
}

// Searchable multi-select dropdown component for genres
function GenreDropdown({
  genres,
  selectedGenres,
  onToggle,
}: {
  genres: string[];
  selectedGenres: Set<string>;
  onToggle: (genre: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const selectedCount = selectedGenres.size;

  // Filter genres by search term
  const filteredGenres = genres.filter((genre) =>
    formatGenreLabel(genre).toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg border transition-colors",
          selectedCount > 0
            ? "bg-white/10 border-white/30 text-white"
            : "bg-white/5 border-white/20 text-white/70 hover:border-white/30",
        )}
      >
        <span className="truncate">
          {selectedCount > 0
            ? `${selectedCount} genre${selectedCount > 1 ? "s" : ""} selected`
            : "Select genres..."}
        </span>
        <ChevronDown
          className={cn(
            "w-4 h-4 shrink-0 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 bottom-full left-0 right-0 mb-1 bg-neutral-900 border border-white/20 rounded-lg shadow-xl overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-white/10">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search genres..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1.5 text-sm bg-white/5 border border-white/10 rounded text-white placeholder:text-white/40 focus:outline-none focus:border-white/30"
            />
          </div>

          {/* Genre list */}
          <div className="max-h-48 overflow-y-auto">
            {filteredGenres.length === 0 ? (
              <div className="px-3 py-4 text-sm text-white/50 text-center">
                No genres found
              </div>
            ) : (
              filteredGenres.map((genre) => {
                const isSelected = selectedGenres.has(genre);
                return (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => onToggle(genre)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                      isSelected
                        ? "bg-white/10 text-white"
                        : "text-white/70 hover:bg-white/5 hover:text-white",
                    )}
                  >
                    <span
                      className={cn(
                        "w-4 h-4 shrink-0 rounded border flex items-center justify-center",
                        isSelected
                          ? "bg-white border-white"
                          : "border-white/30 bg-transparent",
                      )}
                    >
                      {isSelected && (
                        <Check
                          className="w-3 h-3 text-neutral-900"
                          strokeWidth={3}
                        />
                      )}
                    </span>
                    <span>{formatGenreLabel(genre)}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface QuestFilterPanelProps {
  onClose?: () => void;
  showCloseButton?: boolean;
  /** Compact variant for use in dialogs - no background/border, no search field */
  variant?: "default" | "compact";
}

// Mapping of filter keys to control names for analytics
const filterKeyToControl: Partial<Record<keyof FilterOptions, string>> = {
  gameTitle: "GameTitleSearch",
  platforms: "PlatformFilter",
  rewards: "RewardFilter",
  questTypes: "QuestTypeFilter",
  genres: "GenreFilter",
  hideCompleted: "HideCompletedToggle",
};

export function QuestFilterPanel({
  onClose,
  showCloseButton = false,
  variant = "default",
}: QuestFilterPanelProps) {
  const isCompact = variant === "compact";
  const {
    filters,
    setFilters,
    totalQuestCount,
    availablePlatforms,
    availableGenres,
    availableRewards,
    availableQuestTypes,
    resetFilters,
  } = useQuestFilter();
  const { track } = useQuestAnalytics();

  const handleResetFilters = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: "ClearAllFilters",
        controlType: ControlType.BUTTON,
      },
    });
    resetFilters();
  };

  // Generic toggle function for any filter type
  const toggleFilter = (filterKey: keyof FilterOptions, value: string) => {
    setFilters((prev) => {
      const currentSet = prev[filterKey] as Set<string>;
      const newSet = new Set(currentSet);
      const isRemoving = newSet.has(value);

      if (isRemoving) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }

      track({
        action: Action.PRESSED,
        extras: {
          id: "quest-finder",
          control: filterKeyToControl[filterKey] || "",
          controlType: ControlType.BUTTON,
          filterValue: value,
          filterAction: isRemoving ? "removed" : "added",
        },
      });

      return { ...prev, [filterKey]: newSet };
    });
  };

  // debounced tracking for game title search
  const debouncedTrackGameTitleSearch = useCallback(
    debounce((value: string) => {
      track({
        action: Action.PRESSED,
        extras: {
          id: "quest-finder",
          control: filterKeyToControl.gameTitle || "",
          controlType: ControlType.BUTTON,
          filterValue: value,
          filterAction: "searching",
        },
      });
    }, 1000),
    [],
  );

  const handleGameTitleChange = (value: string) => {
    setFilters((prev) => ({ ...prev, gameTitle: value }));
    if (value) debouncedTrackGameTitleSearch(value);
  };

  // Handle clearing game title
  const handleClearGameTitle = () => {
    setFilters((prev) => ({ ...prev, gameTitle: "" }));

    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: filterKeyToControl.gameTitle || "",
        controlType: ControlType.BUTTON,
        filterValue: "",
        filterAction: "cleared",
      },
    });
  };

  // Handle hideCompleted toggle with tracking
  const handleHideCompletedToggle = (checked: boolean) => {
    setFilters((prev) => ({ ...prev, hideCompleted: checked }));

    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: filterKeyToControl.hideCompleted || "",
        controlType: ControlType.BUTTON,
        filterValue: checked ? "true" : "false",
        filterAction: checked ? "enabled" : "disabled",
      },
    });
  };

  const getActiveFilters = () => {
    const active: Array<{
      key: string;
      label: string;
      icon?: React.ReactNode;
      onRemove: () => void;
    }> = [];

    // Platform filters
    for (const platform of filters.platforms) {
      active.push({
        key: `platform-${platform}`,
        label: platform,
        icon: getPlatformIcon(platform),
        onRemove: () => toggleFilter("platforms", platform),
      });
    }

    // Reward filters
    for (const reward of filters.rewards) {
      active.push({
        key: `reward-${reward}`,
        label: getEarningsLabel(reward),
        icon: getEarningsIcon(reward),
        onRemove: () => toggleFilter("rewards", reward),
      });
    }

    // Quest Type filters
    for (const questType of filters.questTypes) {
      active.push({
        key: `questType-${questType}`,
        label: questType,
        onRemove: () => toggleFilter("questTypes", questType),
      });
    }

    // Genre filters
    for (const genre of filters.genres) {
      active.push({
        key: `genre-${genre}`,
        label: formatGenreLabel(genre),
        onRemove: () => toggleFilter("genres", genre),
      });
    }

    // Game Title filter
    if (filters.gameTitle) {
      active.push({
        key: "gameTitle",
        label: `"${filters.gameTitle}"`,
        icon: <Search className="w-4 h-4" />,
        onRemove: handleClearGameTitle,
      });
    }

    return active;
  };

  return (
    <div
      className={cn(
        "space-y-6 h-fit",
        isCompact
          ? "p-4"
          : "bg-neutral-900/95 backdrop-blur-sm border border-white/10 rounded-2xl p-6",
      )}
    >
      {!isCompact && (
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Filter Quests</h2>
          <div className="flex items-center gap-3">
            <p className="text-sm text-white/50">{totalQuestCount} quests</p>
            {showCloseButton && onClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-white/70 hover:text-white transition-colors"
                aria-label="Close filters"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>
      )}
      {/* Active Filters Section */}
      {(() => {
        const activeFilters = getActiveFilters();
        return activeFilters.length > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-white/50">
                Active Filters ({activeFilters.length})
              </p>
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-xs text-white/70 hover:text-white underline transition-colors"
              >
                Clear All
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeFilters.map((filter) => (
                <FilterChip
                  key={filter.key}
                  label={filter.label}
                  selected={true}
                  onToggle={filter.onRemove}
                  icon={filter.icon}
                  removable={true}
                />
              ))}
            </div>
          </div>
        ) : null;
      })()}

      {!isCompact && <div className="w-full h-px bg-white/50 mb-6" />}

      {/* Game Title Search - only in default variant */}
      {!isCompact && (
        <FilterSection title="Search">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              placeholder="Search games and quests..."
              value={filters.gameTitle}
              onChange={(e) => handleGameTitleChange(e.target.value)}
              className="pl-9 bg-white/5 border-white/20 text-white placeholder:text-white/40"
            />
            {filters.gameTitle && (
              <button
                type="button"
                onClick={handleClearGameTitle}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </FilterSection>
      )}

      {/* Platform (OS-based with real icons) */}
      {availablePlatforms.length > 0 && (
        <FilterSection title="Platform">
          <div className="flex flex-wrap gap-2">
            {availablePlatforms.map((platform) => (
              <FilterChip
                key={platform}
                label={platform}
                selected={filters.platforms.has(platform)}
                onToggle={() => toggleFilter("platforms", platform)}
                icon={getPlatformIcon(platform)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Rewards */}
      {availableRewards.length > 0 && (
        <FilterSection title="Rewards">
          <div className="flex flex-wrap gap-2">
            {availableRewards.map((reward) => (
              <FilterChip
                key={reward}
                label={getEarningsLabel(reward)}
                selected={filters.rewards.has(reward)}
                onToggle={() => toggleFilter("rewards", reward)}
                icon={getEarningsIcon(reward)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Quest Type */}
      {availableQuestTypes.length > 0 && (
        <FilterSection title="Quest Type">
          <div className="flex flex-wrap gap-2">
            {availableQuestTypes.map((questType) => (
              <FilterChip
                key={questType}
                label={questType}
                selected={filters.questTypes.has(questType)}
                onToggle={() => toggleFilter("questTypes", questType)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Genre - Searchable dropdown */}
      {availableGenres.length > 0 && (
        <FilterSection title="Genre">
          <GenreDropdown
            genres={availableGenres}
            selectedGenres={filters.genres}
            onToggle={(genre) => toggleFilter("genres", genre)}
          />
          {/* Show selected genres as removable tags */}
          {filters.genres.size > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {Array.from(filters.genres).map((genre) => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => toggleFilter("genres", genre)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white text-neutral-900 hover:bg-white/80 transition-colors"
                >
                  {formatGenreLabel(genre)}
                  <X className="w-2.5 h-2.5" strokeWidth={3} />
                </button>
              ))}
            </div>
          )}
        </FilterSection>
      )}

      <ToggleSwitch
        label="Hide Quests I've Completed"
        checked={filters.hideCompleted}
        onChange={handleHideCompletedToggle}
      />

      {showCloseButton && onClose && (
        <div className="flex gap-3 lg:hidden">
          <Button
            onClick={resetFilters}
            variant="outline"
            size="lg"
            className="flex-1 border-neutral-700 rounded-xl"
          >
            Clear All
          </Button>
          <Button
            onClick={onClose}
            variant="default"
            size="lg"
            className="flex-1 rounded-xl"
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
}
